import {
    Component,
    ComponentInterface,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Method,
    Prop,
    Watch,
} from "@stencil/core";
import { Direction, FormatFn, ParseFn } from "../input-field-number/interfaces";
import { isKeyboardClickKey } from "./utils";
import { clamp, isValidNumber, toMaxPrecision } from "../../utils/numbers";

@Component({
    tag: "uic-stepper",
    styleUrl: "stepper.scss",
})
export class Stepper implements ComponentInterface {
    private timerId: ReturnType<typeof setTimeout>;

    private isStepUpDisabled = false;
    private isStepDownDisabled = false;

    private input: HTMLUicInputFieldNumberElement;

    @Element() host: HTMLUicStepperElement;

    /**
     * If set to `true`, clamps the value to `min` and `max` after losing focus.
     * The value will be clamped to each bound only if the bound is defined.
     */
    @Prop() clampOnBlur = false;

    /**
     * The default value of the input after initialization.
     * Also used in combination with `clampOnBlur` to reset to a valid value.
     */
    @Prop() defaultValue?: number;

    /**
     * Set to `true` to disable the input.
     */
    @Prop() disabled = false;

    /**
     * The label.
     */
    @Prop() label?: string;

    /**
     * The locale to use for input validation.
     */
    @Prop() locale?: string;

    /**
     * Information with low emphasis.
     */
    @Prop() helperText?: string;

    /**
     * If set to `true`, the state is considered invalid.
     */
    @Prop({ mutable: true }) invalid = false;

    /**
     * Information to show if the value is invalid.
     */
    @Prop() invalidText?: string;

    /**
     * Controls if field is marked as requiring some input in case user has
     * not entered anything.
     */
    @Prop() required: boolean = false;

    /**
     * Controls if field is marked as having changed since last save. This is
     * about some visual indicator, only. The actual saving is beyond the scope
     * of this component.
     */
    @Prop() unsaved: boolean = false;

    /**
     * The minimum value.
     */
    @Prop() min?: number;

    /**
     * The maximum value.
     */
    @Prop() max?: number;

    /**
     * The placeholder text.
     */
    @Prop() placeholder: string;

    /**
     * The maximum number of decimal places.
     * Set to `0` to only allow integer values.
     */
    @Prop() precision = 0;

    /**
     * The increment size to use when using keyboard navigation.
     */
    @Prop() step: number = 1;

    /**
     * A valid number or `Number.NaN`.
     */
    @Prop({ mutable: true }) value: number;

    /**
     * The unit of the value, e.g "mm", "kg".
     */
    @Prop() unit?: string;

    /**
     * If this property is set, the minus/plus buttons
     * are replaced by buttons that instantly
     * set the minimum/maximum value if the current value
     * is equal to the min or max value.
     */
    @Prop() quickMinMax?: boolean;

    /**
     * A function that returns a string representation of a given number.
     */
    @Prop() formatter: FormatFn;

    /**
     * A function that parses a number from a given string.
     */
    @Prop() parser: ParseFn;

    /**
     * Emitted when the value has changed.
     */
    @Event() uicChange: EventEmitter;

    @Watch("disabled")
    handleDisabledChanged() {
        this.checkDisabled();
        this.clearStepTimer();
    }

    /**
     * Performs a validity check on the input and sets `invalid` accordingly.
     */
    @Method()
    async reportValidity() {
        this.input.reportValidity();
        this.invalid = this.input.invalid;
    }

    componentWillRender() {
        if (!isValidNumber(this.value)) {
            this.value = this.defaultValue;
        }

        this.checkDisabled();
    }

    disconnectedCallback() {
        this.clearStepTimer();
    }

    private checkDisabled() {
        this.isStepUpDisabled =
            this.disabled ||
            (this.max == null ? false : this.value >= this.max);
        this.isStepDownDisabled =
            this.disabled ||
            (this.min == null ? false : this.value <= this.min);
    }

    private setStepTimer(direction: Direction) {
        const loopStep = () => {
            this.checkDisabled();
            if (this.isStepDownDisabled || this.isStepUpDisabled) {
                return;
            }
            this.handleButtonClick(direction);
            this.timerId = setTimeout(loopStep, 100);
        };

        this.timerId = setTimeout(loopStep, 600);
    }

    private clearStepTimer() {
        clearTimeout(this.timerId);
    }

    private getStepDelta(direction: Direction) {
        return direction * this.step;
    }

    private incrementValue(delta: number) {
        const currentValue = isValidNumber(this.value)
            ? this.value
            : this.defaultValue
            ? this.defaultValue
            : 0;

        let nextValue = toMaxPrecision(currentValue + delta, this.precision);
        nextValue = clamp(nextValue, this.min, this.max);

        this.value = nextValue;
        this.uicChange.emit();
    }

    private handleButtonClick(direction: Direction) {
        let delta;
        if (direction === Direction.MIN) {
            delta = this.min - this.max;
        } else if (direction === Direction.MAX) {
            delta = this.max - this.value;
        } else {
            delta = this.getStepDelta(direction);
        }
        this.incrementValue(delta);
    }

    private getButtonEventHandlers(direction: Direction) {
        return {
            onMouseUp: () => this.clearStepTimer(),
            onMouseLeave: () => this.clearStepTimer(),
            onMouseDown: () => {
                this.handleButtonClick(direction);
                this.setStepTimer(direction);
            },
            onKeyDown: (e: KeyboardEvent) => {
                if (isKeyboardClickKey(e.code)) {
                    this.handleButtonClick(direction);
                }
            },
        };
    }

    private handleInputChange(e: Event) {
        this.value = (e.target as HTMLUicInputFieldNumberElement).value;
    }

    render() {
        const increaseButtonProps = this.getButtonEventHandlers(Direction.UP);
        const decreaseButtonProps = this.getButtonEventHandlers(Direction.DOWN);
        const minButtonProps = this.getButtonEventHandlers(Direction.MIN);
        const maxButtonProps = this.getButtonEventHandlers(Direction.MAX);
        const checkIncreaseDecreaseButtons =
            this.quickMinMax &&
            isValidNumber(this.min) &&
            isValidNumber(this.max) &&
            isValidNumber(this.value);

        return (
            <Host
                class={{
                    "uic-stepper": true,
                    "uic-stepper--has-label": !!this.label,
                    "uic-stepper--invalid": this.invalid,
                }}
            >
                <div class="uic-stepper__label">{this.label}</div>
                <div class="uic-stepper__controls">
                    <uic-input-field-number
                        class="uic-stepper__input"
                        ref={(el) => (this.input = el)}
                        clampOnBlur={this.clampOnBlur}
                        defaultValue={this.defaultValue}
                        disabled={this.disabled}
                        invalid={this.invalid}
                        required={this.required}
                        unsaved={this.unsaved}
                        locale={this.locale}
                        min={this.min}
                        max={this.max}
                        precision={this.precision}
                        placeholder={this.placeholder}
                        step={this.step}
                        unit={this.unit}
                        value={this.value}
                        formatter={this.formatter}
                        parser={this.parser}
                        onUicChange={(e) => this.handleInputChange(e)}
                    ></uic-input-field-number>
                    <uic-button
                        tabindex="-1"
                        icon="uic:minus"
                        size="icon-stretched"
                        disabled={this.isStepDownDisabled}
                        class={
                            checkIncreaseDecreaseButtons &&
                            this.value === this.min
                                ? "uic-stepper__decrease uic-hidden"
                                : "uic-stepper__decrease"
                        }
                        {...decreaseButtonProps}
                    ></uic-button>
                    <uic-button
                        tabindex="-1"
                        icon="uic:double-left"
                        size="icon-stretched"
                        disabled={this.isStepDownDisabled}
                        class={
                            !checkIncreaseDecreaseButtons ||
                            this.value < this.max ||
                            this.value > this.max
                                ? "uic-stepper__decrease_to_min uic-hidden"
                                : "uic-stepper__decrease_to_min"
                        }
                        {...minButtonProps}
                    ></uic-button>
                    <uic-button
                        tabindex="-1"
                        icon="uic:double-right"
                        size="icon-stretched"
                        disabled={this.isStepUpDisabled}
                        class={
                            !checkIncreaseDecreaseButtons ||
                            this.value > this.min ||
                            this.value < this.min
                                ? "uic-stepper__increase_to_max uic-hidden"
                                : "uic-stepper__increase_to_max"
                        }
                        {...maxButtonProps}
                    ></uic-button>
                    <uic-button
                        tabindex="-1"
                        icon="uic:plus"
                        size="icon-stretched"
                        disabled={this.isStepUpDisabled}
                        class={
                            checkIncreaseDecreaseButtons &&
                            this.value === this.max
                                ? "uic-stepper__increase uic-hidden"
                                : "uic-stepper__increase"
                        }
                        {...increaseButtonProps}
                    ></uic-button>
                </div>
                <div class="uic-stepper__helper-text">
                    {this.invalid ? this.invalidText : this.helperText}
                </div>
            </Host>
        );
    }
}
